import React from "react";

/**
 * Test result with explicit parts for assertion-style tests:
 * - expected: what we expect (criteria or expected value)
 * - actual: what we got (processed/derived data)
 * - raw_data: direct JSON response from HTTP GET/POST (unmodified API response)
 */
export interface TestResult {
  success: boolean;
  message: string;
  /** Part 1: expected value or assertion criteria. */
  expected?: unknown;
  /** Part 2: actual/processed value or derived data. */
  actual?: unknown;
  /** Part 3: direct JSON return from HTTP GET/POST (raw API response). */
  raw_data?: unknown;
  /** @deprecated Use actual. Shown as Actual when actual is undefined. */
  data?: unknown;
  error?: string;
  timestamp: number;
}

interface TestResultItemProps {
  result: TestResult;
}

function hasValue(v: unknown): boolean {
  return v !== undefined && v !== null && (typeof v !== "object" || Object.keys(v as object).length > 0);
}

export const TestResultItem: React.FC<TestResultItemProps> = ({ result }) => {
  const showExpected = hasValue(result.expected);
  const actualValue = result.actual !== undefined ? result.actual : result.data;
  const showActual = hasValue(actualValue);
  const showRawData = hasValue(result.raw_data);

  return (
    <div
      className={`sdk-test-result-item ${result.success ? "success" : "error"}`}
    >
      <div className="sdk-test-result-content">
        <div className="sdk-test-result-message">
          {result.success ? "✅" : "❌"} {result.message}
        </div>
        {result.error && (
          <div className="sdk-test-result-error">Error: {result.error}</div>
        )}
        {(showExpected || showActual || showRawData) && (
          <div className="sdk-test-result-two-parts">
            {showExpected && (
              <details className="sdk-test-result-expected">
                <summary>Expected</summary>
                <pre>{JSON.stringify(result.expected, null, 2)}</pre>
              </details>
            )}
            {showActual && (
              <details className="sdk-test-result-actual">
                <summary>Actual</summary>
                <pre>{JSON.stringify(actualValue, null, 2)}</pre>
              </details>
            )}
            {showRawData && (
              <details className="sdk-test-result-raw-data">
                <summary>Raw response</summary>
                <pre>{JSON.stringify(result.raw_data, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>
      <div className="sdk-test-result-timestamp">
        {new Date(result.timestamp).toLocaleTimeString()}
      </div>
    </div>
  );
};

